/**
 * Biometric / passkey login enrollment hand-off (native only).
 *
 * LoginView stashes the technician phone after a successful password login.
 * TechnicianApp reads the stash once mounted and offers enrollment in a local
 * dialog (never blocks navigation). Passwords are never passed through this
 * hand-off — quick login uses passkey or refresh-token + biometric gate.
 */
const BIOMETRIC_DISMISSED_KEY = 'biometric_prompt_dismissed'
/** Fast sync flag: biometric login was enabled on this device (phone hint). */
const BIOMETRIC_CONFIGURED_KEY = 'biometric_login_configured'
/** Session-only stash between LoginView and TechnicianApp (cleared after read). */
const PENDING_BIOMETRIC_ENROLL_KEY = 'pending_biometric_enroll'

export function isBiometricDismissed() {
  try {
    return localStorage.getItem(BIOMETRIC_DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

export function setBiometricDismissed() {
  try {
    localStorage.setItem(BIOMETRIC_DISMISSED_KEY, '1')
  } catch { /* ignore */ }
}

export function clearBiometricDismissed() {
  try {
    localStorage.removeItem(BIOMETRIC_DISMISSED_KEY)
  } catch { /* ignore */ }
}

/**
 * Sync flag for login-screen button (survives Keystore read races on Android).
 * @param {string} phone E.164 phone
 */
export function markBiometricLoginConfigured(phone) {
  if (!phone) return
  try {
    localStorage.setItem(BIOMETRIC_CONFIGURED_KEY, String(phone).trim())
  } catch { /* ignore */ }
}

/** @returns {string|null} */
export function getBiometricConfiguredPhone() {
  try {
    const v = localStorage.getItem(BIOMETRIC_CONFIGURED_KEY)
    return v && v.trim() ? v.trim() : null
  } catch {
    return null
  }
}

export function clearBiometricLoginConfigured() {
  try {
    localStorage.removeItem(BIOMETRIC_CONFIGURED_KEY)
  } catch { /* ignore */ }
}

/**
 * Stash phone for the technician shell to offer biometric enrollment.
 * @param {string} phone normalized phone (E.164)
 */
export function queueBiometricEnrollment(phone) {
  if (!phone) return
  try {
    sessionStorage.setItem(
      PENDING_BIOMETRIC_ENROLL_KEY,
      JSON.stringify({ phone }),
    )
  } catch { /* best-effort */ }
}

/** @deprecated alias — passkey enrollment uses the same stash. */
export function queuePasskeyEnrollment(phone) {
  queueBiometricEnrollment(phone)
}

/**
 * Read and clear the one-time enrollment stash (if any).
 * @returns {{phone: string}|null}
 */
export function readPendingBiometricEnrollment() {
  let raw = null
  try { raw = sessionStorage.getItem(PENDING_BIOMETRIC_ENROLL_KEY) } catch { return null }
  if (!raw) return null
  try { sessionStorage.removeItem(PENDING_BIOMETRIC_ENROLL_KEY) } catch { /* ignore */ }
  try {
    const { phone } = JSON.parse(raw)
    if (phone) return { phone }
  } catch { /* ignore */ }
  return null
}

/** @deprecated alias */
export function readPendingPasskeyEnrollment() {
  return readPendingBiometricEnrollment()
}

export { BIOMETRIC_DISMISSED_KEY, PENDING_BIOMETRIC_ENROLL_KEY, BIOMETRIC_CONFIGURED_KEY }
